'use client';

import { useEffect, useState } from 'react';
import {
  createSvgPreviewUrl,
  inspectSvgMarkup,
  readSvgFileAsText,
  type SvgSource,
} from '@/lib/browser-svg-tools';

export type LoadedSvgSource = SvgSource & {
  file: File | null;
  previewUrl: string;
  originalSize: number;
  sanitizedSize: number;
  inputMode: 'file' | 'paste';
};

function getMarkupSize(markup: string) {
  return new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }).size;
}

export function useLoadedSvgSource() {
  const [source, setSource] = useState<LoadedSvgSource | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  function setNextSource(nextSource: LoadedSvgSource) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function loadFile(file: File) {
    const markup = await readSvgFileAsText(file);
    const inspected = inspectSvgMarkup(markup);
    const previewUrl = createSvgPreviewUrl(inspected.sanitizedMarkup);

    setNextSource({
      ...inspected,
      name: file.name,
      file,
      previewUrl,
      originalSize: file.size,
      sanitizedSize: getMarkupSize(inspected.sanitizedMarkup),
      inputMode: 'file',
    });
  }

  function loadMarkup(markup: string, name = 'inline.svg') {
    const inspected = inspectSvgMarkup(markup);
    const previewUrl = createSvgPreviewUrl(inspected.sanitizedMarkup);

    setNextSource({
      ...inspected,
      name,
      file: null,
      previewUrl,
      originalSize: getMarkupSize(markup),
      sanitizedSize: getMarkupSize(inspected.sanitizedMarkup),
      inputMode: 'paste',
    });
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }

  return {
    source,
    loadFile,
    loadMarkup,
    clearSource,
  };
}
